import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { getErrorMessage } from '@/lib/api'
import { chatService } from '@/services/chat'
import { useAuthStore } from '@/stores/auth-store'
import type { ChatConversation, ChatMessage } from '@/types/chat'

type UseJobChatOptions = {
  jobId: number | string | null
  open: boolean
}

const POLL_INTERVAL_MS = 4000

function sortMessagesOldestFirst(messages: ChatMessage[]): ChatMessage[] {
  return [...messages].sort((left, right) => {
    const leftTime = Date.parse(left.created_at)
    const rightTime = Date.parse(right.created_at)
    if (leftTime !== rightTime) {
      return leftTime - rightTime
    }
    return left.id.localeCompare(right.id)
  })
}

export function useJobChat({ jobId, open }: UseJobChatOptions) {
  const currentUserId = useAuthStore((state) => state.user?.id)
  const [conversation, setConversation] = useState<ChatConversation | null>(null)
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [loading, setLoading] = useState(false)
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [draft, setDraft] = useState('')
  const initializedRef = useRef(false)

  const refreshMessages = useCallback(async (conversationId: string, silent = false) => {
    if (!silent) {
      setLoading(true)
    }
    try {
      const page = await chatService.listMessages(conversationId, { limit: 100 })
      const sorted = sortMessagesOldestFirst(page.results.filter((message) => !message.deleted_at))
      setMessages(sorted)
      setError(null)

      const latest = sorted[sorted.length - 1]
      if (latest) {
        await chatService.markRead(conversationId, latest.id)
      }
    } catch (err) {
      if (!silent) {
        setError(getErrorMessage(err))
      }
    } finally {
      if (!silent) {
        setLoading(false)
      }
    }
  }, [])

  const bootstrap = useCallback(async () => {
    if (!jobId) {
      return
    }

    setLoading(true)
    setError(null)
    try {
      const nextConversation = await chatService.getJobConversation(jobId)
      if (!nextConversation) {
        throw new Error('Could not open job chat.')
      }
      setConversation(nextConversation)
      await refreshMessages(nextConversation.id)
    } catch (err) {
      setError(getErrorMessage(err))
    } finally {
      setLoading(false)
    }
  }, [jobId, refreshMessages])

  useEffect(() => {
    if (!open) {
      initializedRef.current = false
      return
    }

    if (!initializedRef.current) {
      initializedRef.current = true
      void bootstrap()
    }
  }, [open, bootstrap])

  useEffect(() => {
    if (!open || !conversation?.id) {
      return
    }

    const intervalId = window.setInterval(() => {
      void refreshMessages(conversation.id, true)
    }, POLL_INTERVAL_MS)

    return () => {
      window.clearInterval(intervalId)
    }
  }, [open, conversation?.id, refreshMessages])

  const sendMessage = useCallback(async () => {
    const trimmed = draft.trim()
    if (!trimmed || !conversation?.id || sending) {
      return
    }

    setSending(true)
    setError(null)
    const optimisticId = crypto.randomUUID()
    const optimisticMessage: ChatMessage = {
      id: optimisticId,
      conversation_id: conversation.id,
      sender_user_id: Number(currentUserId ?? 0),
      message_type: 'text',
      body: trimmed,
      client_message_id: optimisticId,
      created_at: new Date().toISOString(),
    }

    setMessages((current) => sortMessagesOldestFirst([...current, optimisticMessage]))
    setDraft('')

    try {
      const saved = await chatService.sendMessage(conversation.id, trimmed, optimisticId)
      if (saved) {
        setMessages((current) =>
          sortMessagesOldestFirst(
            current.map((message) => (message.id === optimisticId ? saved : message)),
          ),
        )
        await chatService.markRead(conversation.id, saved.id)
      } else {
        await refreshMessages(conversation.id, true)
      }
    } catch (err) {
      setMessages((current) => current.filter((message) => message.id !== optimisticId))
      setDraft(trimmed)
      setError(getErrorMessage(err))
    } finally {
      setSending(false)
    }
  }, [conversation?.id, currentUserId, draft, refreshMessages, sending])

  const isOwnMessage = useCallback(
    (message: ChatMessage) => String(message.sender_user_id) === String(currentUserId ?? ''),
    [currentUserId],
  )

  const visibleMessages = useMemo(
    () => messages.filter((message) => message.body || message.system_event),
    [messages],
  )

  return {
    conversation,
    messages: visibleMessages,
    loading,
    sending,
    error,
    draft,
    setDraft,
    sendMessage,
    isOwnMessage,
    reload: bootstrap,
  }
}
